/**
 * Remember which marketing page sent a visitor to signup, so the signup
 * event can credit it. The landing site passes `landing_page` on every link
 * to the app. Only the path of a myculture.ai page is kept, never a query
 * string, fragment or any signup detail.
 */
const STORAGE_KEY = "myculture:landing-attribution";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const SITE_HOSTS = new Set(["www.myculture.ai", "myculture.ai"]);

type LandingAttribution = { landing_path: string; capturedAt: number };

let current: LandingAttribution | null = null;

export function toLandingPath(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || !SITE_HOSTS.has(url.hostname)) return null;
    if (url.username || url.password || /@|%40/i.test(url.pathname)) return null;
    return url.pathname.slice(0, 200) || "/";
  } catch (_error) {
    return null;
  }
}

export function captureLandingAttribution(): void {
  if (typeof window === "undefined") return;
  const path = toLandingPath(new URLSearchParams(window.location.search).get("landing_page"));
  if (!path) return;
  current = { landing_path: path, capturedAt: Date.now() };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch (_error) {
    // Memory still preserves attribution when browser storage is unavailable.
  }
}

export function getLandingAttribution(): Record<string, string> {
  if (typeof window === "undefined") return {};
  if (!current) {
    try {
      current = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
    } catch (_error) {
      current = null;
    }
  }
  if (
    !current || typeof current.landing_path !== "string" ||
    !current.landing_path.startsWith("/") || current.landing_path.length > 200 ||
    !Number.isFinite(current.capturedAt) ||
    Date.now() < current.capturedAt || Date.now() - current.capturedAt > TTL_MS
  ) {
    current = null;
    return {};
  }
  return { landing_path: current.landing_path };
}

// Capture the entry URL before the router removes its query parameters.
captureLandingAttribution();
